import { useState, useEffect, useCallback } from 'react';
import photoService from '../services/photoService';

/**
 * Hook específico para operações CRUD de Places com estrutura normalizada
 * Gerencia: { professionals, services, payments, places }
 *
 * @param {Object} service - placesService
 * @returns {Object} Estado normalizado e métodos CRUD
 */
export const usePlacesCRUD = (service) => {
  const [data, setData] = useState({
    professionals: [],
    services: [],
    payments: [],
    places: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await service.getAll();

      // Adicionar photoTimestamps do localStorage aos profissionais
      const professionalsWithTimestamps = (result.professionals || []).map(p => ({
        ...p,
        photoTimestamp: photoService.getPhotoTimestamp(p.id)
      }));
      
      // Garantir estrutura normalizada
      setData({
        professionals: professionalsWithTimestamps,
        services: result.services || [],
        payments: result.payments || [],
        places: (result.places || []).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      });
    } catch (err) {
      console.error('Erro ao carregar dados de places:', err);
      setError(err);
      setData({
        professionals: [],
        services: [],
        payments: [],
        places: []
      });
    } finally {
      setLoading(false);
    }
  }, [service]);

  const createPlace = useCallback(async (placeData) => {
    const result = await service.create(placeData);
    await loadData(); // Recarrega tudo após criar
    return result;
  }, [service, loadData]);

  const updatePlace = useCallback(async (id, placeData) => {
    const result = await service.update(id, placeData);
    await loadData(); // Recarrega tudo após atualizar
    return result;
  }, [service, loadData]);

  const deletePlace = useCallback(async (id) => {
    const result = await service.delete(id);
    await loadData(); // Recarrega tudo após deletar
    return result;
  }, [service, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    data,                    // Estrutura normalizada completa
    places: data.places,     // Atalho para places
    loading,
    error,
    reload: loadData,
    create: createPlace,
    update: updatePlace,
    delete: deletePlace,
  };
};

export default usePlacesCRUD;
